import { FC, useEffect, useState } from "react";
import CustomButton from "../../UI/Buttons/CustomButton";
import LeftArrow from "../../UI/Icons/LeftArrow";
import { useNavigate } from "react-router";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import CandidateItem from "./CandidateItem";
import {
  getAllCandidatesThunk,
  getCandidateDataForCentreThunk,
} from "../../../store/centers/thunks";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../constants/app";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { PaginationLink } from "../../../store/superadmin/types";
import { SortOrder } from "../../../store/superadmin/referral/types";
import { CandidatesSortField, ICandidate } from "../../../store/centers/types";
import ViewProfile from "./PopupModal/ViewProfile";
import CandidateFilter from "./CandidateFilter";
import UnblockCandidate from "./PopupModal/UnblockCandidate";
import BlockReason from "./PopupModal/BlockReason";
import storage from "../../../utils/storage";
import { STORAGE_KEYS } from "../../../constants/storageKeys";

const BlockedCandidates: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allBlockedCandidatesLists = useSelector(
    (state) => state.Centres.allBlockedCandidates
  );
  const isShowNotification = useSelector(
    (state) => state.app.isShowNotification
  );

  const [showUnblockModal, setShowUnblockModal] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [showBlockReasonModal, setShowBlockReasonModal] =
    useState<boolean>(false);

  const [getCandidateDetails, setGetCandidateDetails] =
    useState<ICandidate | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");

  const [selectedSortBy, setSelectedSortBy] = useState<string>("first_name");
  const [selectedSortOrder, setSelectedSortOrder] = useState<SortOrder>("asc");

  const [currentBlockedPage, setCurrentBlockedPage] = useState<number>(1);
  const [blockedCandidateTotalPages, setBlockedCandidateTotalPages] =
    useState<number>(1);
  const [blockedCandidatePaginationLinks, setBlockedCandidatePaginationLinks] =
    useState<PaginationLink[]>([]);
  const [blockedPerPage, setBlockedPerPage] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );

  const [selectedPositionsLevels, setSelectedPositionsLevels] = useState<
    number[]
  >([]);

  const [selectWorkedAtPreviously, setSelectWorkedAtPreviously] = useState<boolean>(false);
  const [selectedDistancyAway, setSelectedDistancyAway] = useState<number>(10);

  const getAllCandidates = async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getAllCandidatesThunk({
        search_from: "blocked",
        keyword: selectedKeyword,
        preferred_page: 1,
        approved_page: 1,
        blocked_page: currentBlockedPage,
        preferred_per_page: 10,
        approved_per_page: 10,
        blocked_per_page: blockedPerPage,
        preferred_sort_by: "",
        preferred_sort_order: "",
        approved_sort_by: "",
        approved_sort_order: "",
        blocked_sort_by: selectedSortBy,
        blocked_sort_order: selectedSortOrder,
        selected_positions_levels: selectedPositionsLevels,
        centre_uuid: storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID),
        worked_with_previously: selectWorkedAtPreviously,
        distance_away: selectedDistancyAway
      })
    );
    dispatch(hideTransparentPreloaderAction());
  };

  useEffect(() => {
    if (!isShowNotification) {
      getAllCandidates();
    }
  }, [
    currentBlockedPage,
    blockedPerPage,
    selectedSortBy,
    selectedSortOrder,
    selectedKeyword,
    isShowNotification,
    selectedPositionsLevels,
    storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID),
    selectWorkedAtPreviously,
    selectedDistancyAway
  ]);

  const handleUnblock = (id: number) => {
    const getCandidateDetails = allBlockedCandidatesLists?.data?.find(
      (allBlockedCandidatesList: ICandidate) =>
        allBlockedCandidatesList.user_id === id
    );
    setGetCandidateDetails(getCandidateDetails ? getCandidateDetails : null);
    setShowUnblockModal(!false);
  };

  const handleProfile = async (id: string) => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(getCandidateDataForCentreThunk({ candidate_id: String(id), centre_id: storage.get(STORAGE_KEYS.SELECTED_CENTRE_ID) }));
    dispatch(hideTransparentPreloaderAction());
    setShowProfileModal(!false);
  };

  const onClickClose = () => {
    setShowUnblockModal(false);
    setShowProfileModal(false);
    setShowBlockReasonModal(false);
  };

  const redirectToCandidates = () => {
    navigate(AUTH_ROUTE.CLIENT_CANDIDATES);
  };

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch); // Update the selected keyword state
      setCurrentBlockedPage(1); // Reset the current page to 1
    }
  }, [debouncedSearch]);

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (allBlockedCandidatesLists && allBlockedCandidatesLists?.total > 0) {
      setBlockedCandidateTotalPages(allBlockedCandidatesLists?.last_page || 1);
      setBlockedCandidatePaginationLinks(allBlockedCandidatesLists?.links);
    }
  }, [allBlockedCandidatesLists]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("blocked_page");
      setCurrentBlockedPage(Number(page));
    }
  };

  // Handle sorting logic
  const handleSortChange = (field: CandidatesSortField) => {
    if (selectedSortBy === field) {
      setSelectedSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSelectedSortBy(field);
      setSelectedSortOrder("asc");
    }
  };

  const handleViewBlockedReason = (uuid: string) => {
    const getCandidateDetails = allBlockedCandidatesLists?.data?.find(
      (allBlockedCandidatesList: ICandidate) =>
        allBlockedCandidatesList.uuid === uuid
    );
    setGetCandidateDetails(getCandidateDetails ? getCandidateDetails : null);
    setShowBlockReasonModal(!false);
  };

  return (
    <>
      <ViewProfile
        showProfileModal={showProfileModal}
        onClickClose={onClickClose}
      />

      {/* Candidates - Unblock Modal */}
      <UnblockCandidate
        showUnblockModal={showUnblockModal}
        onClickClose={onClickClose}
        getCandidateDetails={getCandidateDetails}
        setShowUnblockModal={setShowUnblockModal}
      />
      {/* Candidates - Unblock Modal */}

      <BlockReason
        showBlockReasonModal={showBlockReasonModal}
        onClickClose={onClickClose}
        getCandidateDetails={getCandidateDetails}
      />

      <div className="d-flex flex-column gap-20">
        <div className="d-flex gap-20 align-items-center">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-off-white text-black"
            onClick={redirectToCandidates}
          >
            <LeftArrow />
            Back
          </CustomButton>
          <div className="fs-22 fw-400 text-black">
            {allBlockedCandidatesLists && allBlockedCandidatesLists?.total > 0
              ? allBlockedCandidatesLists?.total
              : 0}{" "}
            Blocked Candidates
          </div>
        </div>
        <div className="d-flex flex-column gap-10">
          <CandidateFilter
            setSearchInput={setSearchInput}
            selectedPositionsLevels={selectedPositionsLevels}
            selectWorkedAtPreviously={selectWorkedAtPreviously}
            selectedDistancyAway={selectedDistancyAway}
            setSelectedPositionsLevels={setSelectedPositionsLevels}
            setSelectWorkedAtPreviously={setSelectWorkedAtPreviously}
            setSelectedDistancyAway={setSelectedDistancyAway}
          />
          <div className="d-flex flex-column">
            <CandidateItem
              type="blocked"
              selectedSortBy={selectedSortBy}
              selectedSortOrder={selectedSortOrder}
              handleSortChange={handleSortChange}
              candidateDatas={
                allBlockedCandidatesLists ? allBlockedCandidatesLists : null
              }
              handleBlock={handleUnblock}
              handleProfile={handleProfile}
              handleViewBlockedReason={handleViewBlockedReason}
              currentPage={currentBlockedPage}
              totalPages={blockedCandidateTotalPages}
              paginationLinks={blockedCandidatePaginationLinks}
              onPageChange={handlePageChange}
              setPerPageList={setBlockedPerPage}
              setCurrentPage={setCurrentBlockedPage}
              getAllCandidates={getAllCandidates}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default BlockedCandidates;
